import { useState } from 'react';
import { useEditMode } from '@/context/EditModeContext';
import { useSiteData } from '@/context/SiteDataContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

/**
 * SectionToggle
 * -----------
 * A tiny "hide / show this section" pill that only renders in Edit Mode.
 * State lives inside `page_content[pageSlug].hidden_sections` (array of section keys).
 * On the public site, the section itself checks `hidden_sections.includes(sectionKey)`
 * and returns null, so admins hide/show without deleting content.
 */
export function useSectionHidden(pageSlug, sectionKey) {
  const { pageContent } = useSiteData();
  const hidden = pageContent?.[pageSlug]?.hidden_sections || [];
  return hidden.includes(sectionKey);
}

export default function SectionToggle({ pageSlug, sectionKey, label = 'section' }) {
  const { editMode } = useEditMode();
  const { pageContent, loadPageContent } = useSiteData();
  const [busy, setBusy] = useState(false);
  if (!editMode) return null;

  const current = pageContent?.[pageSlug]?.hidden_sections || [];
  const isHidden = current.includes(sectionKey);

  const toggle = async () => {
    setBusy(true);
    const next = isHidden ? current.filter(k => k !== sectionKey) : [...current, sectionKey];
    try {
      await api.patch(`/site/page-content/${pageSlug}`, { hidden_sections: next });
      loadPageContent?.(pageSlug);
      toast.success(isHidden ? `Shown: ${label}` : `Hidden: ${label}`);
    } catch { toast.error('Toggle failed'); }
    finally { setBusy(false); }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`section-toggle-${sectionKey}`}
      title={isHidden ? `Show ${label}` : `Hide ${label}`}
      className={`fixed-none z-30 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest shadow-lg transition ${
        isHidden ? 'bg-red-600 text-white' : 'bg-gold text-royal-dark'
      }`}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : (isHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />)}
      {isHidden ? 'Hidden' : 'Visible'} · {label}
    </button>
  );
}
